/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import {
  Component,
  effect,
  input,
  output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarOptions,
  CalendarTask,
  CommonUtils,
  Time,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { CalendarView } from '../calendar-view';
import { CalendarViewDayComponent } from '../calendar-view-day/calendar-view-day.component';
import dayjs, { Dayjs } from 'dayjs';
import jsPDF from 'jspdf';
import { Telework } from '../telework';

@Component({
  selector: 'infl-calendar-view-week',
  templateUrl: './calendar-view-week.component.html',
  styleUrls: ['./calendar-view-week.component.scss'],
  standalone: true,
  imports: [CalendarViewDayComponent],
})
export class CalendarViewWeekComponent implements CalendarView {
  public options = input<CalendarOptions>();
  public days = input<{
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  }>();
  public fortnight = input(false);
  public tasks = input<CalendarTask[]>();
  public date = input<Date>();
  public canDrag = input(true);
  public canResize = input(true);
  public canSelect = input(true);
  public reduce = input(false);
  public dragByOutside = input<CalendarDropTask<any>>();
  public teleworks = input<Telework[]>(null);
  public resource = input<string>(null);
  public viewTotalTasksDuration = input(false);

  public dragTask = output<{
    task: CalendarTask;
    shift: number;
  }>();
  public dropOutside = output<{ date: Date; time: Time }>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<Date>();
  public taskClick = output<CalendarClickEvent>();
  public taskHover = output<CalendarTask>();
  public taskDblClick = output<CalendarClickEvent>();
  public periodSelection = output<WeekSelectionEvent>();
  public periodPreselection = output<WeekSelectionEvent>();
  public taskContextMenu = output<{
    task: CalendarTask;
    style: any;
  }>();
  public clickOnSelection = output<WeekSelectionEvent>();
  public dblClickOnSelection = output<WeekSelectionEvent>();

  public weekDays: { date: Date; tasks: CalendarTask[] }[] = [];
  public draggingTask: CalendarTask;

  private selectedView: CalendarViewDayComponent;
  private dragShift: number;
  private week = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  @ViewChildren('view') dayViews: QueryList<CalendarViewDayComponent>;

  constructor() {
    effect(() => {
      if (this.date() && this.tasks()) {
        this.createDays();
        this.generateTasks();
        this.draggingTask = null;
      }
    });
  }

  public generateTasks() {
    this.clear();
    for (const task of this.tasks()) {
      if (this.checkDay(task.dateStart.isoWeekday())) {
        const weekDay = this.weekDays.find(
          (wd) =>
            wd.date.toDateString() === task.dateStart.toDate().toDateString(),
        );
        if (weekDay) {
          weekDay.tasks.push(task);
        }
      }
    }
    this.weekDays[0].tasks = this.weekDays[0].tasks.slice(0);
  }

  onDayDropOutside(day: { date: Date; tasks: CalendarTask[] }, time: Time) {
    if (this.draggingTask) {
      let m = dayjs(day.date);
      const minutes = time.hours * 60 + time.minutes;
      m = m.hour(Math.floor(minutes / 60)).minute(minutes % 60);
      this.draggingTask.updateDateStart(m);
      this.generateTasks();
      this.taskUpdate.emit(this.draggingTask);
    } else {
      this.dropOutside.emit({ date: day.date, time: time });
    }
  }

  deselect(): void {
    if (this.selectedView) {
      this.selectedView.deselect();
      this.selectedView = null;
    }
  }

  onHoursSelection(
    view: CalendarViewDayComponent,
    selection: WeekSelectionEvent,
  ) {
    if (this.selectedView && this.selectedView !== view) {
      this.selectedView.deselect();
    }
    this.selectedView = view;
    this.periodSelection.emit(selection);
  }

  onDragTask(event: { task: CalendarTask; shift: number }) {
    this.draggingTask = event.task;
    this.dragShift = event.shift;
    this.dragTask.emit(event);
  }

  onClickOnSelection(
    view: CalendarViewDayComponent,
    selection: WeekSelectionEvent,
    dblClick?: boolean,
  ) {
    if (this.selectedView && this.selectedView !== view) {
      this.selectedView.deselect();
    }
    this.selectedView = view;
    if (dblClick) {
      this.dblClickOnSelection.emit(selection);
    } else {
      this.clickOnSelection.emit(selection);
    }
  }

  print(event: WeekSelectionEvent) {
    this.periodPreselection.emit(event);
  }

  findTask(id: string): { style: any; task: CalendarTask } {
    return null;
  }

  getScrollPosition(): { x: number; y: number } {
    return null;
  }

  setScrollPosition(x: number, y: number) {
    // TODO NOTHING
  }

  private initDays(start: Dayjs, i: number, fortnight: boolean) {
    const week = this.options().week.dayOfWeek;
    for (const d in week) {
      if (week[d]) {
        const date = start.clone().isoWeekday(i + 1);
        this.weekDays.push({ date: date.toDate(), tasks: [] });
      }
      ++i;
    }
    if (fortnight) {
      this.initDays(start, i, false);
    }
  }

  private createDays() {
    this.weekDays = [];
    this.initDays(dayjs(this.date()), 0, this.fortnight());
  }

  /**
   * Check if day is displayed
   * @param day
   * @private
   */
  private checkDay(day: number): boolean {
    const name = this.week[day - 1];
    return this.options().week.dayOfWeek[name];
  }

  private clear() {
    for (const d of this.weekDays) {
      d.tasks = [];
    }
  }

  public scrollToDate(date: Date): void {
    const view = this.dayViews.find((v) =>
      CommonUtils.compareDate(v.day(), date),
    );
    view.scrollToDate(date);
  }

  public toPdf(
    doc: jsPDF,
    width: number,
    x: number,
    height?: number,
    last?: boolean,
  ) {
    const dayWidth = width / this.dayViews.length;
    let xDay = x;
    this.dayViews.forEach((dv) => {
      dv.toPdf(doc, dayWidth, xDay);
      xDay += dayWidth;
    });
  }
}
